import React, { useState, useEffect } from 'react';
import { formatFileSize } from '../../utils/fileSize';
import { getImageDimensions } from '../../utils/imageUtils';

interface ImagePreviewProps {
  original: File | null;
  processed: string | null;
}

export function ImagePreview({ original, processed }: ImagePreviewProps) {
  const [originalUrl, setOriginalUrl] = useState<string | null>(null);
  const [originalDims, setOriginalDims] = useState<{ width: number; height: number } | null>(null);
  const [processedDims, setProcessedDims] = useState<{ width: number; height: number } | null>(null);

  useEffect(() => {
    if (!original) {
      setOriginalUrl(null);
      setOriginalDims(null);
      return;
    }

    const url = URL.createObjectURL(original);
    setOriginalUrl(url);

    getImageDimensions(original).then(setOriginalDims);

    return () => {
      URL.revokeObjectURL(url);
    };
  }, [original]);

  useEffect(() => {
    if (!processed) {
      setProcessedDims(null);
      return;
    }

    const img = new Image();
    img.onload = () => {
      setProcessedDims({ width: img.width, height: img.height });
    };
    img.src = processed;
  }, [processed]);

  if (!original) return null;

  const processedSizeInBytes = processed
    ? Math.round((processed.split(',')[1] || '').length * 0.75)
    : 0;

  const sizeReduction = processed && original.size > 0
    ? Math.round(((original.size - processedSizeInBytes) / original.size) * 100)
    : null;

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-1">
      <div className="bg-white dark:bg-slate-900 p-3 rounded-2xl border border-slate-200 dark:border-slate-800 shadow-sm space-y-2">
        <div className="flex justify-between items-center">
          <h3 className="text-xs font-bold text-slate-800 dark:text-slate-200">Original Photo</h3>
          {originalDims && (
            <span className="text-[11px] bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-400 px-2 py-0.5 rounded-md font-mono font-bold">
              {originalDims.width} × {originalDims.height} px
            </span>
          )}
        </div>
        <div className="relative h-48 sm:h-56 bg-slate-950/80 rounded-xl overflow-hidden border border-slate-200/50 dark:border-slate-800 flex items-center justify-center p-2">
          {originalUrl && (
            <img
              src={originalUrl}
              alt="Original"
              className="object-contain max-h-full max-w-full rounded-lg"
            />
          )}
        </div>
        <div className="flex justify-between items-center text-xs text-slate-600 dark:text-slate-400 font-medium">
          <span>File Size:</span>
          <span className="font-bold text-slate-900 dark:text-slate-100">{formatFileSize(original.size)}</span>
        </div>
      </div>
      
      <div className="bg-white dark:bg-slate-900 p-3 rounded-2xl border border-slate-200 dark:border-slate-800 shadow-sm space-y-2">
        <div className="flex justify-between items-center">
          <h3 className="text-xs font-bold text-slate-800 dark:text-slate-200">Processed Output</h3>
          {processedDims && (
            <span className="text-[11px] bg-indigo-50 dark:bg-indigo-950 text-indigo-700 dark:text-indigo-300 px-2 py-0.5 rounded-md font-mono font-bold">
              {processedDims.width} × {processedDims.height} px
            </span>
          )}
        </div>
        <div className="relative h-48 sm:h-56 bg-slate-950/80 rounded-xl overflow-hidden border border-slate-200/50 dark:border-slate-800 flex items-center justify-center p-2">
          {processed ? (
            <img
              src={processed}
              alt="Processed"
              className="object-contain max-h-full max-w-full rounded-lg"
            />
          ) : (
            <div className="text-slate-400 text-xs text-center px-4">
              Apply a crop or resize setting above to preview output
            </div>
          )}
        </div>
        {processed ? (
          <div className="flex justify-between items-center text-xs text-slate-600 dark:text-slate-400 font-medium">
            <div className="flex items-center gap-1.5">
              <span>File Size:</span>
              {sizeReduction !== null && sizeReduction > 0 && (
                <span className="text-[10px] font-bold text-emerald-600 dark:text-emerald-400 bg-emerald-50 dark:bg-emerald-950/80 px-2 py-0.5 rounded-full">
                  -{sizeReduction}%
                </span>
              )}
            </div>
            <span className="font-bold text-indigo-600 dark:text-indigo-400">{formatFileSize(processedSizeInBytes)}</span>
          </div>
        ) : (
          <div className="text-xs text-slate-400 italic text-right">Pending changes</div>
        )}
      </div>
    </div>
  );
}